(ns dashboard.views.analysis.analysis-bank.view
  (:require ["react" :as react]
            [dashboard.components.breadcrumbs :refer [breadcrumbs]]
            [dashboard.components.paginated-list.view :as paginated-list]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.modals :as analysis.modals]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.entity.analysis.search :as analysis.search]
            [dashboard.entity.analysis.tree :as analysis.tree]
            [dashboard.entity.organization.db :as org.db]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [dashboard.subs :as common-subs]
            [dashboard.views.analysis.analysis-bank.config :as config]
            [dashboard.views.analysis.analysis-bank.events :as events]
            [dashboard.views.analysis.analysis-bank.subs :as subs]
            [dashboard.views.analysis.common :as analysis]
            [dashboard.views.analysis.synchronization.events :as sync-events]
            [dashboard.views.analysis.synchronization.subs :as sync-subs] ;; Note that synch view is required here in order to load the ::show-modal event definition
            [dashboard.views.analysis.synchronization.view]
            [io.repliance.util.tree :as tree]
            [re-frame.core :as rf]))

(def controllers
  [{:start (fn []
             (analysis.db/get-structured-analyses false)
             (analysis.db/get-applied-analyses false)
             (analysis.db/get-structured-analysis-questions false)
             (analysis.db/get-categories false)
             (org.db/fetch-organization)
             (rf/dispatch [::questionnaire-list/get {:template true}]))}])

(defn make-column-config [hidden-roots set-hidden-roots]
  [analysis/construct-id-column
   {:label "Construct"
    :id :construct
    :sort-key analysis/construct-sort-key
    :render (fn [row]
              [:f> analysis/ConstructColumn row hidden-roots set-hidden-roots])}
   {:id :actions
    :label ""
    :render (fn [analysis]
              [:div.level
               [:button.button.is-small.is-rounded.is-circular.is-info
                {:title "Record a sub-analysis"
                 :on-click #(rf/dispatch [::events/show-record-structured-analysis-modal analysis])}
                [:span.icon>i.fas.fa-plus]]])
    :features #{:structured-analysis-creation}}
   {:label "Category"
    :id :caterogy
    :sort-key analysis/category-sort-key
    :render analysis/CategoryColumn
    :features #{:applied-analysis-creation :structured-analysis-creation}}
   {:id :states
    :label  "Possible states"
    :sort-key :states
    :render (fn [{:keys [states]}]
              [:<> (map (fn [state]
                          #^{:key (str "state-" (first state))}
                            [:span.mr-2
                             [analysis.profile/state-tag {:class ["is-small"]} state]])
                        states)])
    :features #{:structured-analysis-creation}}
   {:id :state
    :label "State"
    :sort-key analysis/state-sort-key
    :render analysis/StateColumn
    :features #{:applied-analysis-creation}}])

(defn structure-view []
  (let [features-enabled @(rf/subscribe [::common-subs/features-enabled])
        structured-analysis-creation-enabled? @(rf/subscribe [::common-subs/feature-enabled :structured-analysis-creation])
        applied-analysis-creation-enabled? @(rf/subscribe [::common-subs/feature-enabled :applied-analysis-creation])
        include-initialized-analyses @(rf/subscribe [::subs/include-initialized-analyses])
        items @(analysis.db/structured-analyses-with-applied-joined*)
        user-preferences @(rf/subscribe [::common-subs/user-preferences])
        roots (set (keep #(when-not (:parent-id %)
                            (:construct-id %))
                         items))
        [hidden-roots set-hidden-roots] (react/useState (if (:initial-analysis-collapsed-state user-preferences)
                                                          roots
                                                          #{}))]
    [:div
     [breadcrumbs {:items (config/breadcrumbs)}]
     [:div.section
      [:p.title.is-3 (if applied-analysis-creation-enabled?
                       "Applied Analyses"
                       "Analysis Structures")]
      [:f> paginated-list/paginated-list
       {:view-name config/view-id
        :allow-tree-sort true
        :tree-sort-toggle-column-id :construct-id
        :default-view-options {:tree-sort true}
        :header-content (let [synchronizing? @(rf/subscribe [::sync-subs/is-synchronizing])]
                          [:<>
                           (when applied-analysis-creation-enabled?
                             [:button.button.is-rounded {:title "Check for structured analysis updates. You'll have a chance to review the changes before you commit."
                                                         :class (when synchronizing? "is-loading")
                                                         :on-click #(rf/dispatch [::sync-events/sync-from-master])}
                              [:span.icon.mr-1>i.fas.fa-arrows-rotate]
                              "Update analysis structures"])
                           (when structured-analysis-creation-enabled?
                             [:button.button.ml-2.is-rounded {:on-click #(rf/dispatch [::events/show-record-structured-analysis-modal nil])}
                              [:span.icon.mr-1>i.fas.fa-plus]
                              "Record new analysis structure"])])
        :table-class []
        :columns (filter (fn [{:keys [features]}]
                           (if features
                             (some features features-enabled)
                            ;; Allow by default
                             true))
                         (make-column-config hidden-roots set-hidden-roots))
        :hide-pagination? true
        :row-props-transform (fn [{:keys [applied-analyses] :as structured-analysis} props _index]
                               (if applied-analysis-creation-enabled?
                                 (cond
                                   (empty? applied-analyses)
                                   (assoc-in props [:style :background-color] "#c0c0c0")

                                   (analysis.db/is-latest-asa-outdated? structured-analysis applied-analyses)
                                   (assoc props :class "has-background-warning-light"))
                                 props))
        :search-enabled true
        :search-extras (fn []
                         (let [collapsed-roots (= hidden-roots roots)]
                           [:div.is-flex.is-align-items-center
                            [:button.button.is-rounded.ml-2
                             {:title (if collapsed-roots
                                       "Expand root nodes"
                                       "Collapse root nodes")
                              :on-click (fn [_]
                                          (if collapsed-roots
                                            (set-hidden-roots #{})
                                            (set-hidden-roots roots)))}
                             (if collapsed-roots
                               [:span.icon>i.fas.fa-solid.fa-expand]
                               [:span.icon>i.fas.fa-solid.fa-compress])]
                            (when applied-analysis-creation-enabled?
                              [:button.button.is-rounded.ml-2
                               {:on-click #(rf/dispatch [::events/toggle-include-initialized-analyses])
                                :title "Toggle showing analyses in \"Not Yet Evaluated\" state"}
                               (if include-initialized-analyses
                                 [:span.icon>i.fas.fa-toggle-on]
                                 [:span.icon>i.fas.fa-toggle-off])])]))}
       {:items items
        :loading @(analysis.db/loading-structured-or-applied*)
        :data-transform (fn [items sort-keyfn sort-comp tree-sort]
                          (if tree-sort
                            (tree/sort-hierarchical items sort-keyfn sort-comp :construct-id)
                            (map #(assoc % :tree/show-ancestors true)
                                 (sort-by sort-keyfn sort-comp (tree/sort-hierarchical items nil nil :construct-id)))))
        :data-filter (fn [search-query {:tree/keys [ancestors] :as structured-analysis}]
                       (and (if include-initialized-analyses
                              true
                              (not (analysis/in-initial-state? (first (:applied-analyses structured-analysis)))))
                            (analysis/search-query-filter search-query structured-analysis)
                            (if (and (seq hidden-roots)
                                     (some ancestors hidden-roots))
                              false
                              true)))
        :default-sort-keyfn :construct-id
        :default-sort-order :asc}]]]))

(defn view []
  (let [applied-analysis-creation-enabled?
        @(rf/subscribe [::common-subs/feature-enabled :applied-analysis-creation])]
    (if-not applied-analysis-creation-enabled?
      [:f> structure-view]
      [:div.analysis-bank
       [breadcrumbs {:items (config/breadcrumbs)}]
       [:div.section
        [:div.level.mb-0
         [:p.title.is-3
          (if applied-analysis-creation-enabled?
            "Applied Analyses"
            "Analysis Structures")]
         (let [synchronizing? @(rf/subscribe [::sync-subs/is-synchronizing])]
           (when applied-analysis-creation-enabled?
             [:button.button.is-rounded
              {:title "Check for structured analysis updates. You'll have a chance to review the changes before you commit."
               :class (when synchronizing? "is-loading")
               :on-click #(rf/dispatch [::sync-events/sync-from-master])}
              [:span.icon.mr-1>i.fas.fa-arrows-rotate]
              "Update analysis structures"]))]
        [:f> analysis.search/AnalysisSearch
         {:category "Policy"
          :path [:analysis-bank :search]
          :preselected-version-ids #{}
          :selected-version-ids #{}
          :set-use-hierarchy-context identity
          :show-invalid-filter true
          :on-click-construct (fn [construct-id structure]
                                (analysis.modals/show-editor-modal
                                 :view
                                 construct-id
                                 (-> structure :analyses first)))
          :on-click-state (fn [construct-id structure]
                            (analysis.modals/show-editor-modal
                             :edit
                             construct-id
                             (-> structure :applied-analyses first)))
          :row-component analysis.tree/ListItemAnalysisRow}]]])))
